"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * AuthBadge - the rounded icon medallion that crowns an auth card's heading.
 *
 * Unifies the two badges the unified screens hand-rolled: ForgotPassword's
 * `MailBadge` (a blue-tinted tile, `bg-[#eaf1fe] text-forest`) and
 * ResetPassword's `LockBadge` (which also had a `sage` success variant,
 * `bg-[#e7f0e4] text-sage`). Re-expressed in brand tokens:
 *   - `tone="accent"` (default) - the blue "in progress / informational" tile
 *     (the old `forest` mail badge), using `--accent`.
 *   - `tone="success"` - the green "done / verified" tile (the old `sage` lock
 *     badge), using `--success`.
 *
 * Pass any node as `icon` (typically a lucide icon or an inline SVG sized
 * ~22px). Decorative by default (`aria-hidden`) since the heading carries the
 * meaning; pass `aria-hidden={false}` + a label if you need it announced.
 *
 *   <AuthBadge tone="accent" icon={<Mail className="h-[22px] w-[22px]" />} />
 *   <AuthBadge tone="success" icon={<Lock className="h-[22px] w-[22px]" />} />
 */
export const authBadgeVariants = cva(
  "grid h-[46px] w-[46px] place-items-center rounded-xl",
  {
    variants: {
      tone: {
        /** Blue informational tile (was the `forest` mail badge). */
        accent: "bg-accent/10 text-accent",
        /** Green success tile (was the `sage` lock badge). */
        success: "bg-success/10 text-success",
      },
    },
    defaultVariants: { tone: "accent" },
  },
);

export interface AuthBadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof authBadgeVariants> {
  /** The glyph to render inside the medallion. */
  icon: React.ReactNode;
}

export const AuthBadge = React.forwardRef<HTMLDivElement, AuthBadgeProps>(
  ({ className, tone, icon, "aria-hidden": ariaHidden = true, ...props }, ref) => (
    <div
      ref={ref}
      aria-hidden={ariaHidden}
      className={cn(authBadgeVariants({ tone }), className)}
      {...props}
    >
      {icon}
    </div>
  ),
);
AuthBadge.displayName = "AuthBadge";
